import { ColumnDef, RowData } from '@tanstack/react-table'
import { Settings } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ClinicUser } from '@magnetar/data-access'
import { Clinic, User, UserRole } from '@magnetar/admin-api-interfaces'
import { useState } from 'react'
import { ClinicUserRoleDialog } from '@/pages/clinic/clinicUsers/ClinicUserRoleDialog'
import { EditClinicUserDialog } from '@/pages/clinic/clinicUsers/EditClinicUserDialog'

declare module '@tanstack/table-core' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    reloadData?: () => void
  }
}

type ClinicUserRow = ClinicUser & {
  user: User
  clinic: Clinic
  user_role?: UserRole | null
}

export const columns: ColumnDef<ClinicUser & { user: User; clinic: Clinic }>[] = [
  {
    accessorKey: 'clinic_user_id',
    header: 'ID',
  },
  {
    accessorKey: 'user.account',
    header: '帳號',
  },
  {
    accessorKey: 'user.user_name',
    header: '姓名',
  },
  {
    accessorKey: 'clinic.clinic_name',
    header: '所屬診所',
    cell: ({ row }) => {
      const clinic = row.original.clinic
      return (
        <div>
          <div>{clinic?.clinic_name}</div>
          {clinic?.alias && (
            <div className="text-sm text-muted-foreground">{clinic.alias}</div>
          )}
        </div>
      )
    },
  },
  {
    accessorKey: 'user_role.role_name',
    header: '角色',
    cell: ({ row }) => {
      const userRole = (row.original as ClinicUserRow).user_role
      return userRole?.role_name || '-'
    },
  },
  {
    accessorKey: 'user.is_active',
    header: '狀態',
    cell: ({ row }) => {
      const isActive = row.original.user?.is_active
      return (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}
        >
          {isActive ? '啟用' : '停用'}
        </span>
      )
    },
  },
  {
    id: 'actions',
    cell: function ActionsCell({ row, table }) {
      const clinicUser = row.original
      const [showEditDialog, setShowEditDialog] = useState(false)
      const [showRoleDialog, setShowRoleDialog] = useState(false)

      return (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>操作</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowEditDialog(true)}>
                編輯
              </DropdownMenuItem>
              {clinicUser.user?.user_role?.role_type !== 'investor' && (
                <DropdownMenuItem onClick={() => setShowRoleDialog(true)}>
                  角色設定
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <EditClinicUserDialog
            clinicUser={clinicUser}
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            onSuccess={() => table.options.meta?.reloadData?.()}
          />

          <ClinicUserRoleDialog
            clinicUser={clinicUser}
            open={showRoleDialog}
            onOpenChange={setShowRoleDialog}
            onSuccess={() => table.options.meta?.reloadData?.()}
          />
        </>
      )
    },
  },
]
